import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';
import { clearMixpanelUser } from '@/common/utils/mixpanel';
import api from '@/db/apiClient';
import { ParsedToken, signInWithCustomToken, signOut } from 'firebase/auth';
import { clearLocalStorage } from './localStorage';

export async function handleLogout(redirect: boolean): Promise<void>;
export async function handleLogout(): Promise<void>;
export async function handleLogout(redirect: boolean = true) {
  try {
    await signOut(api.asEducator.auth);
    clearLocalStorage();
    if (redirect) window.location.href = '/';
  } catch (error) {
    console.error('Error signing out', error);
  }
}

export async function handleStudentLogout() {
  try {
    clearMixpanelUser('student');
    await signOut(api.asStudent.auth);
  } catch (error) {
    console.error('Error signing out', error);
  }
}

export async function authenticateStudent(
  username: string,
  password: string,
): Promise<boolean> {
  try {
    // Get custom token
    const { token } = await api.asEducator.getStudentAuthToken({
      username,
      password,
    });

    // Authenticate with token
    await signInWithCustomToken(api.asStudent.auth, token);

    return true;
  } catch {
    return false;
  }
}

export async function refreshUserClaims(
  validate?: (claims: ParsedToken | undefined) => boolean,
): Promise<ParsedToken | undefined> {
  const currentUser = api.asEducator.auth.currentUser;
  if (!currentUser) {
    console.warn('Skipping refresh user claims, no current user');
    return;
  }

  const MAX_RETRIES = 5;
  const BASE_DELAY = 500; // ms

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    await api.asEducator.syncUserClaims();
    await currentUser.reload();
    const token = await currentUser.getIdTokenResult(true);
    const claims = token?.claims;

    if (!validate || validate(claims)) {
      return claims;
    }

    const delay = BASE_DELAY * 2 ** attempt;
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
}

export function enforceAutoLogout(
  keyPrefix: 'student' | 'educator',
  onLogout: () => Promise<void>,
) {
  let autoLogoutAt = getFromLocalStorage(`${keyPrefix}_autoLogout`);

  if (!autoLogoutAt) {
    autoLogoutAt = Date.now() + 8 * 60 * 60 * 1000; // 8 hours
    saveToLocalStorage(`${keyPrefix}_autoLogout`, autoLogoutAt);
    return false;
  }

  if (Date.now() > autoLogoutAt) {
    removeFromLocalStorage(`${keyPrefix}_autoLogout`);
    onLogout();
    return true;
  }

  return false;
}
